import { isEmpty } from "@groww-tech-pvt/ella";
import { axiosInstance, axiosInstanceNoSession } from "../config";
import { setStorage, getStorage, errorInStorage } from "@adapter/web-storage";
import { getAuthToken, getPinToken, setLoginRedirectionUrl } from "../storage";
import { STORAGE_TYPE } from "../storage/constant";
import { ROUTES } from "../constants/routes";
import { getIsDomain915 } from "../config/domainConfig";

export function hasSession(checkFor2FA = false): boolean {
  if (errorInStorage()) return false;

  const authorization = getAuthToken();

  if (checkFor2FA) {
    const pinToken = getPinToken();
    return !isEmpty(authorization) && !isEmpty(pinToken);
  }

  return !isEmpty(authorization);
}


export function validateProofNumber(proofNumber: string, proofType: string) {
  if (proofType === 'PAN') {
    return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(proofNumber);
  }
  if (proofType === 'AADHAAR') {
    return /^[0-9]{4}$/.test(proofNumber);
  }
  if (proofType === 'DL') {
    return /^[A-Z]{2}[0-9]{2}\s?[0-9A-Z]{11,13}$/i.test(proofNumber);
  }

  return true;
}

export function getMaxLengthForIdentityProofType(proofType: string) {
  if (proofType === 'AADHAAR') {
    return 4;
  }
  if (proofType === 'DL') {
    return 15;
  }
  if (proofType === 'PAN') {
    return 10;
  }
  return 0;
}

export function setRedirectionUrl(url?: string, useLocalStorage = true) {
  //Need to check useLocalStorage = true
  let pinRedirectionUrl = (window as any).pinRedirectionUrl;

  try {
    if (typeof window !== 'undefined') {
      if (url) {
        pinRedirectionUrl = window.location.href.replace(
          window.location.href,
          url
        );
      } else {
        pinRedirectionUrl = window.location.href.replace(
          window.location.origin,
          ''
        );
      }
      if (useLocalStorage) {
        setLoginRedirectionUrl(pinRedirectionUrl);
      }
    }
  } catch (error) {
    console.error('Error in setting redirection URL: ', error);
  }
}

export const hashstr = (s: string) => {
  let hash = 0;

  if (s.length === 0) return hash;
  for (let i = 0; i < s.length; i++) {
    const char = s.charCodeAt(i);

    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash;
};

const hitApiStoreInLSAndReturnData = (
  url: string,
  key: string,
  expiresInMin: number,
  useEncryption: boolean,
  apiParams: object,
  noSession?: boolean,
  podAxiosInstance = axiosInstance,
  podAxiosInstanceNoSession = axiosInstanceNoSession
) => {
  if (noSession) {
    return new Promise(function (resolve, reject) {
      podAxiosInstanceNoSession
        .get(url, {
          params: apiParams,
        })
        .then(response => {
          setStorage(
            STORAGE_TYPE.LOCAL_STORAGE,
            key,
            response.data,
            useEncryption,
            expiresInMin
          );
          resolve(response);
        })
        .catch(err => {
          reject(err);
        });
    });
  } else {
    return new Promise(function (resolve, reject) {
      podAxiosInstance
        .get(url, {
          params: apiParams,
        })
        .then(response => {
          const responseObj = { ...response };

          setStorage(
            STORAGE_TYPE.LOCAL_STORAGE,
            key,
            responseObj.data,
            useEncryption,
            expiresInMin
          );
          resolve(responseObj);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
};

export const cacheApiResponseInLocalStorage = (
  url: string,
  data?: {
    expiresInMin?: number;
    useEncryption?: boolean;
    hardRefresh?: boolean;
    noSession?: boolean;
    key?: string;
  },
  apiParams = {},
  podAxiosInstance = axiosInstance,
  podAxiosInstanceNoSession = axiosInstanceNoSession
) => {
  let expiresInMin = 5; // 5 min default
  let useEncryption = false;
  let hardRefresh = false;
  let noSession = false;

  if (data) {
    if (data.expiresInMin) {
      expiresInMin = data.expiresInMin;
    }

    if (data.useEncryption) {
      useEncryption = data.useEncryption;
    }

    if (data.hardRefresh) {
      hardRefresh = data.hardRefresh;
    }

    if (data.noSession) {
      noSession = data.noSession;
    }
  }

  let key: string;

  if (data && data.key) {
    key = data.key;
  } else {
    if (isEmpty(apiParams)) {
      key = hashstr(url).toString();
    } else {
      key = hashstr(url + JSON.stringify(apiParams)).toString();
    }
  }

  // In this case, hit api, update LS, return data
  if (data && hardRefresh) {
    return hitApiStoreInLSAndReturnData(
      url,
      key,
      expiresInMin,
      useEncryption,
      apiParams,
      noSession,
      podAxiosInstance,
      podAxiosInstanceNoSession
    );
  }

  // Fetch the data if available, else hit the API, update the LS and return the data
  const responseData = getStorage(
    STORAGE_TYPE.LOCAL_STORAGE,
    key,
    useEncryption
  );

  if (isEmpty(responseData)) {
    return hitApiStoreInLSAndReturnData(
      url,
      key,
      expiresInMin,
      useEncryption,
      apiParams,
      noSession,
      podAxiosInstance,
      podAxiosInstanceNoSession
    );
  } else {
    return Promise.resolve({ data: responseData });
  }
};

export function getLoginPageURL() {
  return window.location.origin + ROUTES.LOGIN;
}

export function getEnterPinPageURL() {
  const journeyType = new URLSearchParams(window.location.search).get('journeyType');
  if (getIsDomain915() && isEmpty(journeyType)) return window.location.origin + ROUTES.LOGIN;
  return window.location.origin + ROUTES.ENTER_PIN;
}

export const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

export const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    promise,
    wait(ms).then(() => {
      throw new Error('timed out');
    })
  ]);
